import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	useContext as useReactContext,
} from "react";
import { AppContext } from "../../../context/AppContextProvider";
import type { ILocationProps } from "../../../services/Location/types";
import { useLocationStream } from "../../../hooks/useLocationStream";
import { getMoovsecVehiclesAsync } from "../../../services/Location";
import {
	getLicenseId,
	getToken,
	getUserId,
} from "../../../services/DefaultService";

const LocationContext = createContext<ILocationProps[]>([]);
const VEHICLES_POLL_INTERVAL_MS = 20000;
const VEHICLES_POLL_HIDDEN_MS = 60000;
const VEHICLES_POLL_MAX_BACKOFF_MS = 120000;
const VEHICLES_VISIBLE_REFRESH_MIN_MS = 8000;

export const LocationProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const { showSnackbar } = useReactContext(AppContext);
	const [moovsecVehicles, setMoovsecVehicles] = useState<ILocationProps[]>([]);

	const apiBase =
		(window as Window & { _env_?: { REACT_APP_API_URL_HOST?: string } })._env_
			?.REACT_APP_API_URL_HOST || "http://localhost:5298";
	const protocol = apiBase.startsWith("https") ? "wss" : "ws";
	const host = apiBase.replace(/^https?:\/\//, "").replace(/\/$/, "");
	const licenseId = getLicenseId() || "";
	const userId = getUserId() || "";
	const token = getToken();
	const wsUrl = useMemo(() => {
		if (!licenseId || !userId) return "";

		const query = new URLSearchParams({
			licenseId,
			userId,
			...(token ? { token: window.atob(token) } : {}),
		}).toString();

		return `${protocol}://${host}/api/locations/stream?${query}`;
	}, [licenseId, userId, token, protocol, host]);

	const streamLocations = useLocationStream(wsUrl);

	useEffect(() => {
		if (!licenseId) {
			setMoovsecVehicles([]);
			return;
		}

		let mounted = true;
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let isFetching = false;
		let failureCount = 0;
		let lastFetchAt = 0;

		const areSameVehicles = (a: ILocationProps[], b: ILocationProps[]) => {
			if (a.length !== b.length) return false;

			const cameraKey = (item: ILocationProps) =>
				(item.cameras || [])
					.map((camera) =>
						[
							camera.channel,
							camera.cameraId,
							camera.name,
							camera.streamUrl,
							camera.hlsUrl,
							camera.rtspUrl,
							camera.snapshotUrl,
							camera.isOnline,
							camera.status,
						].join("#"),
					)
					.sort()
					.join("|");

			const keyOf = (item: ILocationProps) =>
				[
					item.entityId,
					item.latitude,
					item.longitude,
					item.timestamp,
					item.hasLocation,
					item.vehiclePlate,
					item.modelName,
					item.address,
					cameraKey(item),
				].join("|");

			const bMap = new Map(b.map((item) => [item.entityId, keyOf(item)]));

			for (const item of a) {
				if (bMap.get(item.entityId) !== keyOf(item)) {
					return false;
				}
			}

			return true;
		};

		const getNextDelay = (baseDelayMs: number) => {
			const hiddenDelay =
				document.visibilityState === "hidden"
					? Math.max(baseDelayMs, VEHICLES_POLL_HIDDEN_MS)
					: baseDelayMs;

			if (failureCount <= 0) {
				return hiddenDelay;
			}

			const backoffDelay = Math.min(
				VEHICLES_POLL_MAX_BACKOFF_MS,
				hiddenDelay * 2 ** Math.min(failureCount, 3),
			);

			return backoffDelay;
		};

		const scheduleNext = (delayMs = VEHICLES_POLL_INTERVAL_MS) => {
			if (!mounted) return;
			timeoutId = setTimeout(loadVehicles, getNextDelay(delayMs));
		};

		const loadVehicles = async () => {
			if (isFetching) {
				scheduleNext(VEHICLES_POLL_INTERVAL_MS);
				return;
			}

			isFetching = true;
			lastFetchAt = Date.now();

			try {
				const vehicles = await getMoovsecVehiclesAsync(true);
				failureCount = 0;
				if (mounted) {
					setMoovsecVehicles((prev) =>
						areSameVehicles(prev, vehicles) ? prev : vehicles,
					);
				}
			} catch (error: unknown) {
				failureCount += 1;
				const errorMessage =
					error instanceof Error ? error.message : "Erro interno do servidor!";
				if (
					errorMessage.includes(
						"É necessário realizar a integração com o Moovsec",
					)
				) {
					showSnackbar(errorMessage, "warning");
				}
				console.warn("Falha ao carregar veículos do Moovsec", error);
			} finally {
				isFetching = false;
				scheduleNext(VEHICLES_POLL_INTERVAL_MS);
			}
		};

		const handleVisibilityChange = () => {
			if (!mounted || document.visibilityState !== "visible") {
				return;
			}

			if (Date.now() - lastFetchAt < VEHICLES_VISIBLE_REFRESH_MIN_MS) {
				return;
			}

			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}

			void loadVehicles();
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		loadVehicles();

		return () => {
			mounted = false;

			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [licenseId, showSnackbar]);

	const locations = useMemo(() => {
		const map = new Map<string, ILocationProps>();

		moovsecVehicles.forEach((vehicle) => {
			map.set(vehicle.entityId, {
				...vehicle,
				hasLocation:
					vehicle.hasLocation ??
					!!(vehicle.latitude !== null && vehicle.longitude !== null),
			});
		});

		streamLocations.forEach((location) => {
			const current = map.get(location.entityId);

			const resolvedLatitude =
				typeof location.latitude === "number"
					? location.latitude
					: (current?.latitude ?? null);
			const resolvedLongitude =
				typeof location.longitude === "number"
					? location.longitude
					: (current?.longitude ?? null);
			const resolvedTimestamp =
				location.timestamp ?? current?.timestamp ?? null;
			const resolvedAddress = location.address ?? current?.address;

			map.set(location.entityId, {
				...current,
				...location,
				address: resolvedAddress,
				latitude: resolvedLatitude,
				longitude: resolvedLongitude,
				timestamp: resolvedTimestamp,
				hasLocation:
					typeof location.hasLocation === "boolean"
						? location.hasLocation
						: !!(resolvedLatitude !== null && resolvedLongitude !== null),
			});
		});

		return Array.from(map.values());
	}, [moovsecVehicles, streamLocations]);

	return (
		<LocationContext.Provider value={locations}>
			{children}
		</LocationContext.Provider>
	);
};

export const useLocations = () => useContext(LocationContext);
